import { create } from 'zustand'
import { mockSolicitacoes, mockPrestacoes, mockSaldoMinisterios, mockSaldoSeparado, mockEntradasMinisterios } from '@/mocks'
import type { SolicitacaoVerba, PrestacaoConta, SaldoMinisterio, SaldoSeparado, EntradaMinisterio } from '@/mocks/financas.mock'

interface DespesaOperacional {
  id: string
  tipo: 'energia' | 'agua' | 'telefone' | 'internet' | 'limpeza' | 'seguranca' | 'combustivel' | 'manutencao' | 'outros'
  descricao: string
  valor: number
  data: string
  dataVencimento: string
  responsavel: string
  observacoes?: string
  status: 'pago' | 'pendente' | 'vencido'
}

type State = {
  solicitacoes: SolicitacaoVerba[]
  prestacoes: PrestacaoConta[]
  entradasMinisterios: EntradaMinisterio[]
  saldoMinisterios: SaldoMinisterio[]
  saldoSeparado: SaldoSeparado
  despesasOperacionais: DespesaOperacional[]
  addSolicitacao: (s: Omit<SolicitacaoVerba, 'id' | 'data' | 'status'> & { autoApprove?: boolean }) => void
  updateSolicitacaoStatus: (id: string, status: SolicitacaoVerba['status']) => void
  addPrestacao: (
    p: Omit<PrestacaoConta, 'id' | 'data'> & { notaFile?: File | null }
  ) => Promise<void>
  addEntrada: (args: {
    ministerioId: string
    tipo: 'oferta' | 'cantina' | 'doacao'
    valor: number
    responsavel: string
  }) => void
  addEntradaSeparada: (args: {
    tipo: 'dizimo' | 'ofertas' | 'doacoes' | 'missoes'
    valor: number
    responsavel: string
    observacoes?: string
  }) => void
  addSaidaMissoes: (args: {
    descricao: string
    valor: number
    responsavel: string
    observacoes?: string
  }) => void
  addDespesaOperacional: (despesa: Omit<DespesaOperacional, 'id'>) => void
  updateDespesaStatus: (id: string, status: DespesaOperacional['status']) => void
}

export const useFinancasStore = create<State>((set, get) => ({
  solicitacoes: mockSolicitacoes,
  prestacoes: mockPrestacoes,
  entradasMinisterios: mockEntradasMinisterios,
  saldoMinisterios: mockSaldoMinisterios,
  saldoSeparado: mockSaldoSeparado,
  despesasOperacionais: [],
  addSolicitacao: (s: Omit<SolicitacaoVerba, 'id' | 'data' | 'status'> & { autoApprove?: boolean }) => {
    const id = `s_${Date.now()}`
    const nova: SolicitacaoVerba = {
      id,
      ministerioId: s.ministerioId,
      titulo: s.titulo,
      motivo: s.motivo,
      valor: s.valor,
      solicitante: s.solicitante,
      prioridade: s.prioridade || 'media',
      data: new Date().toISOString().split('T')[0],
      status: s.autoApprove ? 'aprovado' : 'pendente',
    }
    set({ solicitacoes: [nova, ...get().solicitacoes] })
  },
  updateSolicitacaoStatus: (id: string, status: SolicitacaoVerba['status']) => {
    const solicitacao = get().solicitacoes.find((s: SolicitacaoVerba) => s.id === id)
    
    set({
      solicitacoes: get().solicitacoes.map((s: SolicitacaoVerba) =>
        s.id === id ? { ...s, status } : s
      ),
      saldoMinisterios: status === 'aprovado' && solicitacao
        ? (() => {
            const isTransferenciaParaIgreja = solicitacao.titulo.includes('Transferência para Saldo da Igreja')
            
            if (isTransferenciaParaIgreja) {
              return get().saldoMinisterios.map((saldo: SaldoMinisterio) =>
                saldo.ministerioId === solicitacao.ministerioId
                  ? { ...saldo, saldoDisponivel: saldo.saldoDisponivel - solicitacao.valor }
                  : saldo
              )
            } else {
              return get().saldoMinisterios.map((saldo: SaldoMinisterio) =>
                saldo.ministerioId === solicitacao.ministerioId
                  ? { ...saldo, saldoDisponivel: saldo.saldoDisponivel + solicitacao.valor }
                  : saldo
              )
            }
          })()
        : get().saldoMinisterios,
      saldoSeparado: status === 'aprovado' && solicitacao && solicitacao.titulo.includes('Transferência para Saldo da Igreja')
        ? (() => {
            const saldoAtual = get().saldoSeparado
            return {
              ...saldoAtual,
              igreja: {
                ...saldoAtual.igreja,
                saldo: saldoAtual.igreja.saldo + solicitacao.valor
              }
            }
          })()
        : get().saldoSeparado,
    })
  },
  addEntrada: (args: {
    ministerioId: string
    tipo: 'oferta' | 'cantina' | 'doacao'
    valor: number
    responsavel: string
  }) => {
    const { ministerioId, tipo, valor, responsavel } = args
    
    const novaEntrada: EntradaMinisterio = {
      id: `e_${Date.now()}`,
      ministerioId,
      tipo,
      valor,
      responsavel,
      data: new Date().toISOString().split('T')[0],
    }
    
    set({
      entradasMinisterios: [novaEntrada, ...get().entradasMinisterios],
      saldoMinisterios: get().saldoMinisterios.map((s: SaldoMinisterio) =>
        s.ministerioId === ministerioId ? { ...s, saldoDisponivel: s.saldoDisponivel + valor } : s
      ),
    })
  },
  addEntradaSeparada: (args: {
    tipo: 'dizimo' | 'ofertas' | 'doacoes' | 'missoes'
    valor: number
    responsavel: string
    observacoes?: string
  }) => {
    const { tipo, valor } = args
    const saldoAtual = get().saldoSeparado
    
    if (tipo === 'missoes') {
      set({
        saldoSeparado: {
          ...saldoAtual,
          missoes: {
            ...saldoAtual.missoes,
            saldo: saldoAtual.missoes.saldo + valor
          }
        }
      })
    } else {
      set({
        saldoSeparado: {
          ...saldoAtual,
          igreja: {
            ...saldoAtual.igreja,
            saldo: saldoAtual.igreja.saldo + valor
          }
        }
      })
    }
  },
  addSaidaMissoes: (args: {
    descricao: string
    valor: number
    responsavel: string
    observacoes?: string
  }) => {
    const { valor } = args
    const saldoAtual = get().saldoSeparado
    
    if (saldoAtual.missoes.saldo < valor) {
      throw new Error('Saldo insuficiente em missões')
    }
    
    set({
      saldoSeparado: {
        ...saldoAtual,
        missoes: {
          ...saldoAtual.missoes,
          saldo: saldoAtual.missoes.saldo - valor
        }
      }
    })
  },
  addPrestacao: async (p: Omit<PrestacaoConta, 'id' | 'data'> & { notaFile?: File | null }) => {
    const saldoAtual =
      get().saldoMinisterios.find((s: SaldoMinisterio) => s.ministerioId === p.ministerioId)
        ?.saldoDisponivel ?? 0
    if (saldoAtual < p.valor) {
      throw new Error('Saldo insuficiente')
    }

    const id = `p_${Date.now()}`
    let notaFiscalUrl: string | undefined
    if (p.notaFile) {
      notaFiscalUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.readAsDataURL(p.notaFile as File)
      })
    }

    const nova: PrestacaoConta = {
      id,
      ministerioId: p.ministerioId,
      titulo: p.titulo,
      valor: p.valor,
      autor: p.autor,
      data: new Date().toISOString().split('T')[0],
      notaFiscalUrl,
      observacoes: p.observacoes,
    }

    set({
      prestacoes: [nova, ...get().prestacoes],
      saldoMinisterios: get().saldoMinisterios.map((s: SaldoMinisterio) =>
        s.ministerioId === p.ministerioId
          ? { ...s, saldoDisponivel: Number((s.saldoDisponivel - p.valor).toFixed(2)) }
          : s
      ),
    })
  },
  addDespesaOperacional: (despesa: Omit<DespesaOperacional, 'id'>) => {
    const id = `d_${Date.now()}`
    const novaDespesa: DespesaOperacional = {
      id,
      ...despesa,
    }
    set({ despesasOperacionais: [novaDespesa, ...get().despesasOperacionais] })
  },
  updateDespesaStatus: (id: string, status: DespesaOperacional['status']) => {
    set({
      despesasOperacionais: get().despesasOperacionais.map((d: DespesaOperacional) =>
        d.id === id ? { ...d, status } : d
      ),
    })
  },
}))
